/**
 * Signature du mandat a distance — page PUBLIQUE.
 *
 * Le client arrive ici par un lien personnel recu par courriel, parce que son mandat a
 * ete cree depuis le CRM et que personne n'etait la pour lui tendre un telephone. Il
 * n'a pas de compte : la page appelle donc `axios` directement, sans notre client d'API
 * qui pose un jeton d'authentification. Meme choix, et meme raison, que
 * `features/enrollment/pages/EnrollmentPage.jsx`.
 *
 * ⚠️ ELLE EST HORS DE `AppLayout`, ET CE N'EST PAS UN DETAIL DE ROUTAGE. Aucune barre
 * laterale, aucune garde de role, et — consequence utile — aucune visite guidee : le
 * `TourGuide` est monte dans `AppLayout` et sort immediatement sans `role`. Un client
 * qui ouvre son lien ne recevra donc jamais le voile de premiere connexion par-dessus
 * son parapheur.
 *
 * ⚠️ LE PARAPHEUR EST CELUI DU PARCOURS PHYSIQUE, `shared/components/SignatureCanvas`.
 * Un second composant de signature divergerait, et on aurait deux gestes differents
 * pour une meme piece juridique. C'est la meme signature, apposee ailleurs.
 *
 * ⚠️ LE TEXTE DES AUTORISATIONS EST SERVI PAR LE SERVEUR, jamais reecrit ici. C'est de
 * cette chaine exacte qu'on conserve l'empreinte au moment du trait : un texte
 * reformule cote navigateur rendrait la preuve invérifiable, et un consentement dont on
 * ne peut plus dire ce qu'il montrait est un vice de consentement.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Checkbox } from '@/shared/ui/checkbox';
import SignatureCanvas from '@/shared/components/SignatureCanvas';
import { CheckCircle2, Loader2, XCircle, FileSignature } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = {
  PAC: 'Pompe à chaleur', PV: 'Panneaux solaires',
  Ballon: 'Ballon électrique', Batterie: 'Batterie de stockage',
  Radiateur: 'Radiateurs électriques',
};

export default function SignatureMandatPage() {
  const { jeton } = useParams();
  const [chargement, setChargement] = useState(true);
  const [dossier, setDossier] = useState(null);
  const [erreur, setErreur] = useState('');
  const [signe, setSigne] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [paraphe, setParaphe] = useState('');
  // LES TROIS AUTORISATIONS, et elles seules depuis le 02/09. Les cles sont celles que
  // le mandat enregistre : si la correspondance se perd, le client coche une case et le
  // serveur en enregistre une autre.
  const [cases, setCases] = useState({ modulation: false, donnees: false, audit: false });

  useEffect(() => {
    axios.get(`${API}/signature-mandat/${jeton}`)
      .then(r => setDossier(r.data))
      .catch(err => setErreur(err.response?.data?.detail
                              || "Ce lien de signature n'est pas valide."))
      .finally(() => setChargement(false));
  }, [jeton]);

  const toutCoche = Object.values(cases).every(Boolean);

  const signer = async () => {
    setErreur('');
    setEnvoi(true);
    try {
      await axios.post(`${API}/signature-mandat/${jeton}`, {
        signature_data: paraphe,
        consentements: cases,
      });
      setSigne(true);
    } catch (err) {
      setErreur(err.response?.data?.detail
                || "Votre signature n'a pas pu être enregistrée.");
    }
    setEnvoi(false);
  };

  if (chargement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
      </div>
    );
  }

  // Un lien mort n'est pas une panne : c'est un lien mort, et le client doit savoir
  // quoi faire. Le message vient du serveur, qui ne distingue jamais « expiré » de
  // « révoqué » — le savoir n'aiderait que celui qui devine des jetons.
  if (!dossier) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4"
           data-testid="signature-lien-invalide">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-neutral-900 mb-2">
              Lien de signature indisponible
            </h1>
            <p className="text-sm text-neutral-500">{erreur}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signe) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4"
           data-testid="signature-terminee">
        <Card className="max-w-md w-full border-emerald-200">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-neutral-900 mb-2">
              Merci, votre mandat est signé
            </h1>
            <p className="text-sm text-neutral-500">
              Votre installateur en est informé. Vous n'avez rien d'autre à faire, et ce
              lien ne sert plus.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { client, equipements = [], operateur = {}, consentements = {} } = dossier;

  return (
    <div className="min-h-screen bg-neutral-50 py-6 px-4" data-testid="signature-mandat-page">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="text-center">
          <FileSignature className="w-10 h-10 text-neutral-700 mx-auto mb-2" />
          <h1 className="text-xl font-semibold text-neutral-900">
            Votre mandat de flexibilité électrique
          </h1>
          <p className="text-sm text-neutral-500">
            {client?.prenom} {client?.nom}
            {client?.ville ? ` · ${client.ville}` : ''}
            {operateur.nom ? ` · ${operateur.nom}` : ''}
          </p>
        </div>

        {equipements.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h2 className="text-[11px] uppercase tracking-[0.15em] text-neutral-400 mb-2">
                Équipements concernés
              </h2>
              <ul className="space-y-1 text-[13px] text-neutral-700"
                  data-testid="signature-equipements">
                {equipements.map((e, i) => (
                  <li key={i}>
                    {TYPES[e.type] || e.type}
                    {e.marque || e.modele
                      ? ` — ${[e.marque, e.modele].filter(Boolean).join(' ')}` : ''}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* LE TEXTE, SERVI ET NON REECRIT. On l'affiche tel qu'il arrive, bloc par
            bloc : c'est de cette chaine que le serveur conserve l'empreinte au moment
            du trait. */}
        <Card>
          <CardContent className="p-4 space-y-4" data-testid="signature-consentements">
            <p className="text-[13px] font-medium text-neutral-900">
              {consentements.intro}
            </p>
            {(consentements.autorisations || []).map((bloc) => (
              <div key={bloc.id} className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox
                    checked={cases[bloc.id] || false}
                    onCheckedChange={(v) => setCases(c => ({ ...c, [bloc.id]: !!v }))}
                    data-testid={`consentement-${bloc.id}`}
                  />
                  <span className="text-[13px] font-medium text-neutral-800">
                    {bloc.titre}
                  </span>
                </label>
                <ul className="ml-6 list-disc space-y-1 text-[12px] text-neutral-600">
                  {(bloc.alineas || []).map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              </div>
            ))}
            {consentements.partenaires && (
              <details className="text-[12px] text-neutral-600">
                <summary className="cursor-pointer font-medium text-neutral-800">
                  {consentements.partenaires.titre}
                </summary>
                <p className="mt-2">{consentements.partenaires.intro}</p>
                <p className="mt-1">
                  {(consentements.partenaires.operateurs || []).join(', ')}.
                </p>
                <p className="mt-1">{consentements.partenaires.phrase}</p>
              </details>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-2">
            <h2 className="text-[11px] uppercase tracking-[0.15em] text-neutral-400">
              Votre signature
            </h2>
            <SignatureCanvas onSignatureChange={setParaphe} />
          </CardContent>
        </Card>

        {erreur && (
          <p className="text-[13px] text-red-600 text-center" data-testid="signature-erreur">
            {erreur}
          </p>
        )}

        {/* Le bouton dit POURQUOI il est éteint. « Signer » grisé sans motif laisse le
            client chercher ce qu'il a oublié sur une page qu'il découvre. */}
        <Button
          className="w-full h-12 text-[15px]"
          disabled={!toutCoche || !paraphe || envoi}
          onClick={signer}
          data-testid="signature-valider"
        >
          {envoi ? 'Enregistrement…' : 'Signer mon mandat'}
        </Button>
        {(!toutCoche || !paraphe) && (
          <p className="text-[12px] text-neutral-500 text-center"
             data-testid="signature-manque">
            {!toutCoche
              ? 'Cochez les trois autorisations pour pouvoir signer.'
              : 'Tracez votre signature dans le cadre ci-dessus.'}
          </p>
        )}
        {operateur.email_support && (
          <p className="text-[12px] text-neutral-400 text-center">
            Une question ? <a className="underline"
                              href={`mailto:${operateur.email_support}`}>
              {operateur.email_support}</a>
          </p>
        )}
      </div>
    </div>
  );
}
